import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Phone, PhoneCall, Clock, Calendar, Search, BookOpen,
  ChevronLeft, ChevronRight, Trash2, Edit2, Plus, X,
  CheckCircle, AlertCircle, UserPlus, ChevronDown, ChevronUp,
  Save, BarChart3, Lightbulb
} from 'lucide-react';
import TelemarketingKpiDashboard from '@/components/TelemarketingKpiDashboard';
import TelemarketingHintsView from '@/components/TelemarketingHintsView';

const OUTCOMES = ['Appointment Booked', 'Requested Info', 'Follow-Up Scheduled', 'Not Interested', 'No Answer', 'Voicemail Left', 'Call Back Later', 'Wrong Number', 'Not Home Owner', 'Already Have Roofer', 'Other'];
const CITIES = ['Auckland', 'Hamilton', 'Tauranga', 'Wellington', 'Christchurch', 'Dunedin', 'Napier', 'Hastings', 'Palmerston North', 'New Plymouth', 'Rotorua', 'Whangarei'];
import { useRoofTypes } from '@/hooks/useRoofTypes';
const PROPERTY_TYPES = ['Residential', 'Commercial', 'Rental', 'Lifestyle Block', 'Unit/Apartment', 'Other'];

export default function TelemarketingPage() {
  const { api } = useAuth();
  const roofTypes = useRoofTypes(api);
  const [mode, setMode] = useState('workspace'); // workspace | history | scripts | reports
  const [calls, setCalls] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [stats, setStats] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [telemarketers, setTelemarketers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeScript, setActiveScript] = useState(null);
  const [showObjPanel, setShowObjPanel] = useState(false);
  const [callForm, setCallForm] = useState({ call_date: new Date().toISOString().slice(0, 10), call_time: new Date().toTimeString().slice(0, 5) });
  const [showBooking, setShowBooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [editId, setEditId] = useState(null);
  const [scriptForm, setScriptForm] = useState({});
  const [showScriptForm, setShowScriptForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [callRes, scriptRes, statsRes, conRes, tmRes] = await Promise.all([
        api.get(`/telemarketing/calls?page=${page}&search=${search}&limit=15`),
        api.get('/telemarketing/scripts'),
        api.get('/telemarketing/stats'),
        api.get('/contractors'),
        api.get('/telemarketing/telemarketers'),
      ]);
      setCalls(callRes.data.items || []);
      setTotal(callRes.data.total || 0);
      setPages(callRes.data.pages || 1);
      setScripts(scriptRes.data || []);
      setStats(statsRes.data);
      setContractors(conRes.data || []);
      setTelemarketers(tmRes.data || []);
      if (!activeScript && scriptRes.data?.length) setActiveScript(scriptRes.data[0]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, page, search]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const flashMsg = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 3000); };

  const saveCall = async (outcome) => {
    setSaving(true);
    try {
      const payload = { ...callForm, outcome, call_date: callForm.call_date || new Date().toISOString().slice(0, 10), booking_confirmed: outcome === 'Appointment Booked' };
      if (editId) { await api.put(`/telemarketing/calls/${editId}`, payload); flashMsg('Call updated'); }
      else { await api.post('/telemarketing/calls', payload); flashMsg(`Call logged: ${outcome}`); }
      setCallForm({ call_date: new Date().toISOString().slice(0, 10), call_time: new Date().toTimeString().slice(0, 5) });
      setEditId(null);
      setShowBooking(false);
      load();
    } catch (e) { flashMsg('Error saving call'); }
    setSaving(false);
  };

  const quickBook = async () => {
    if (!callForm.contact_name || !callForm.booking_date) { flashMsg('Name and booking date required'); return; }
    setSaving(true);
    try {
      const names = (callForm.contact_name || '').split(' ');
      const payload = {
        first_name: names[0] || '', last_name: names.slice(1).join(' ') || '',
        phone_number: callForm.phone_number || '', mobile_number: callForm.mobile_number || '',
        email: callForm.email || '', address: callForm.address || '',
        suburb: callForm.suburb || '', city: callForm.city || '',
        booking_date: callForm.booking_date, booking_time: callForm.booking_time || '',
        appraiser_id: callForm.appraiser_id || '', appraiser_name: callForm.appraiser_name || '',
        quote_type: 'Appraisal', lead_source: 'Telemarketing',
      };
      await api.post('/telemarketing/quick-book', payload);
      await saveCall('Appointment Booked');
      flashMsg('Appraisal booked and scheduled!');
    } catch (e) { flashMsg('Error booking: ' + (e.response?.data?.detail || '')); }
    setSaving(false);
  };

  const editCall = (c) => { setCallForm({ ...c }); setEditId(c._id); setMode('workspace'); };
  const deleteCall = async (id) => { if (!window.confirm('Delete?')) return; await api.delete(`/telemarketing/calls/${id}`); load(); };

  // Get owner contractor as default appraiser
  const ownerContractor = contractors.find(c => c.is_owner);
  const setDefaultAppraiser = () => {
    if (ownerContractor && !callForm.appraiser_id) {
      setCallForm(prev => ({ ...prev, appraiser_id: ownerContractor._id, appraiser_name: ownerContractor.contractor_name }));
    }
  };

  // Auto-set appraiser on booking open
  const openBooking = () => {
    setDefaultAppraiser();
    setShowBooking(true);
  };

  const saveScript = async (e) => {
    e.preventDefault();
    if (scriptForm._id) await api.put(`/telemarketing/scripts/${scriptForm._id}`, scriptForm);
    else await api.post('/telemarketing/scripts', scriptForm);
    setShowScriptForm(false); setScriptForm({});
    load();
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;

  return (
    <div data-testid="telemarketing-page" className="space-y-3">
      {/* Flash */}
      {flash && <div className="fixed top-4 right-4 z-50 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium shadow-lg animate-pulse">{flash}</div>}

      {/* Header + Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Telemarketing</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            {stats ? <><span className="font-mono-num font-bold text-slate-700">{stats.total_calls}</span> calls · <span className="font-mono-num font-bold text-slate-700">{stats.bookings}</span> bookings · <span className="font-mono-num font-bold text-amber-700">{stats.pending_follow_ups}</span> follow-ups</> : 'Loading stats...'}
          </p>
        </div>
        <div className="flex gap-1 bg-white border border-[#E2E2DF] rounded-md p-0.5 shadow-sm flex-wrap">
          {[
            ['workspace', 'Call Workspace', PhoneCall],
            ['history', 'Call History', Clock],
            ['scripts', 'Scripts', BookOpen],
            ['hints', 'Hints & Rebuttals', Lightbulb],
            ['reports', 'Daily Reports', BarChart3],
          ].map(([m, l, Icon]) => (
            <button key={m} data-testid={`tm-tab-${m}`} onClick={() => setMode(m)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-all ${mode === m ? 'bg-[#D35400] text-white' : 'text-slate-500 hover:text-slate-700'}`}>
              <Icon size={13} />{l}
            </button>
          ))}
        </div>
      </div>

      {/* =============== WORKSPACE MODE =============== */}
      {mode === 'workspace' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* LEFT: Call Form */}
          <div className="lg:col-span-2 space-y-3">
            {/* Quick Entry */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h2 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><Phone size={15} className="text-[#D35400]" /> {editId ? 'Edit Call' : 'New Call'}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <input data-testid="call-contact-name" value={callForm.contact_name || ''} onChange={e => setCallForm({ ...callForm, contact_name: e.target.value })} placeholder="Contact Name *" className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none" />
                <input data-testid="call-phone" value={callForm.phone_number || ''} onChange={e => setCallForm({ ...callForm, phone_number: e.target.value })} placeholder="Phone" className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none" />
                <input value={callForm.mobile_number || ''} onChange={e => setCallForm({ ...callForm, mobile_number: e.target.value })} placeholder="Mobile" className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none" />
                <input value={callForm.email || ''} onChange={e => setCallForm({ ...callForm, email: e.target.value })} placeholder="Email" className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none" />
                <input data-testid="call-address" value={callForm.address || ''} onChange={e => setCallForm({ ...callForm, address: e.target.value })} placeholder="Street Address" className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none" />
                <input value={callForm.suburb || ''} onChange={e => setCallForm({ ...callForm, suburb: e.target.value })} placeholder="Suburb" className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none" />
                <select value={callForm.city || ''} onChange={e => setCallForm({ ...callForm, city: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none">
                  <option value="">City</option>
                  {CITIES.map(c => <option key={c}>{c}</option>)}
                </select>
                <select data-testid="call-roof-type" value={callForm.roof_type || ''} onChange={e => setCallForm({ ...callForm, roof_type: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none">
                  <option value="">Roof Type</option>
                  {[...roofTypes, 'Other', 'Unknown'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <textarea value={callForm.comments || ''} onChange={e => setCallForm({ ...callForm, comments: e.target.value })} placeholder="Notes..." rows={2} className="w-full mt-2 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D35400] focus:outline-none" />

              {/* Quick Outcome Buttons */}
              <div className="flex flex-wrap gap-2 mt-3">
                <button data-testid="outcome-booked" onClick={openBooking} disabled={saving} className="px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 transition-colors flex items-center gap-1.5"><CheckCircle size={13} /> BOOK APPRAISAL</button>
                {['Follow-Up Scheduled', 'Requested Info', 'Not Interested', 'No Answer', 'Call Back Later', 'Voicemail Left'].map(o => (
                  <button key={o} onClick={() => saveCall(o)} disabled={saving} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${o === 'Not Interested' ? 'border-red-200 text-red-600 hover:bg-red-50' : o === 'No Answer' || o === 'Voicemail Left' ? 'border-slate-200 text-slate-500 hover:bg-slate-50' : 'border-blue-200 text-blue-600 hover:bg-blue-50'}`}>{o}</button>
                ))}
              </div>
            </div>

            {/* Booking Panel */}
            {showBooking && (
              <div data-testid="booking-panel" className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-green-800 flex items-center gap-2"><Calendar size={15} /> Book Free Appraisal</h3>
                  <button onClick={() => setShowBooking(false)} className="text-green-600 hover:text-green-800"><X size={16} /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div>
                    <label className="block text-[10px] font-medium text-green-700 mb-0.5">Date *</label>
                    <input data-testid="booking-date" type="date" value={callForm.booking_date || ''} onChange={e => setCallForm({ ...callForm, booking_date: e.target.value })} className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm bg-white" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-green-700 mb-0.5">Time</label>
                    <input type="time" value={callForm.booking_time || ''} onChange={e => setCallForm({ ...callForm, booking_time: e.target.value })} className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm bg-white" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-medium text-green-700 mb-0.5">Appraiser</label>
                    <select value={callForm.appraiser_id || ''} onChange={e => { const c = contractors.find(x => x._id === e.target.value); setCallForm({ ...callForm, appraiser_id: e.target.value, appraiser_name: c ? c.contractor_name : '' }); }} className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm bg-white">
                      <option value="">Select...</option>
                      {contractors.map(c => <option key={c._id} value={c._id}>{c.contractor_name}{c.is_owner ? ' (Owner)' : ''}</option>)}
                    </select>
                  </div>
                </div>
                <button data-testid="confirm-quick-book" onClick={quickBook} disabled={saving} className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition-colors">
                  <UserPlus size={15} /> {saving ? 'Booking...' : 'Create Client + Book Appraisal'}
                </button>
              </div>
            )}

            {/* Today's Calls */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Recent Calls ({total})</span>
                <div className="relative w-48"><Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" /><input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search..." className="w-full pl-7 pr-2 py-1 border border-slate-200 rounded text-xs" /></div>
              </div>
              <div className="max-h-[280px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0">
                    <th className="text-left px-3 py-2">Contact</th><th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">Suburb</th><th className="text-left px-3 py-2">Outcome</th><th className="text-left px-3 py-2">Date</th><th className="px-3 py-2"></th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {calls.map(c => (
                      <tr key={c._id} className="hover:bg-slate-50 cursor-pointer" onClick={() => editCall(c)}>
                        <td className="px-3 py-2 font-medium text-[#0F172A]">{c.contact_name || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{c.phone_number || c.mobile_number || '-'}</td>
                        <td className="px-3 py-2 text-slate-500">{c.suburb || '-'}</td>
                        <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${c.outcome === 'Appointment Booked' ? 'bg-green-100 text-green-700' : c.outcome === 'Not Interested' ? 'bg-red-100 text-red-600' : c.outcome === 'No Answer' ? 'bg-slate-100 text-slate-500' : 'bg-blue-100 text-blue-700'}`}>{c.outcome || '-'}</span></td>
                        <td className="px-3 py-2 text-slate-400">{c.call_date}</td>
                        <td className="px-3 py-1"><button onClick={e => { e.stopPropagation(); deleteCall(c._id); }} className="text-slate-300 hover:text-red-500"><Trash2 size={12} /></button></td>
                      </tr>
                    ))}
                    {calls.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No calls yet</td></tr>}
                  </tbody>
                </table>
              </div>
              {pages > 1 && <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between"><span className="text-[10px] text-slate-400">Page {page}/{pages}</span><div className="flex gap-1"><button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1 rounded border border-slate-200 disabled:opacity-30"><ChevronLeft size={12} /></button><button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1 rounded border border-slate-200 disabled:opacity-30"><ChevronRight size={12} /></button></div></div>}
            </div>
          </div>

          {/* RIGHT: Scripts Panel */}
          <div className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><BookOpen size={13} /> Scripts</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {scripts.map(s => (
                  <button key={s._id} onClick={() => { setActiveScript(s); setShowObjPanel(false); }} className={`w-full text-left px-4 py-2.5 text-xs transition-colors ${activeScript?._id === s._id ? 'bg-[#D35400]/5 text-[#D35400] font-semibold border-l-2 border-[#D35400]' : 'text-slate-600 hover:bg-slate-50'}`}>
                    {s.title}
                    {s.script_type && <span className="ml-2 text-[10px] px-1.5 py-0 rounded-full bg-slate-100 text-slate-500">{s.script_type}</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Script Display */}
            {activeScript && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-sm font-bold text-[#0F172A]">{activeScript.title}</span>
                  {activeScript.objection_rebuttals?.length > 0 && (
                    <button onClick={() => setShowObjPanel(!showObjPanel)} className={`text-[10px] px-2 py-1 rounded-full font-bold transition-colors ${showObjPanel ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                      {showObjPanel ? 'Show Script' : `Objections (${activeScript.objection_rebuttals.length})`}
                    </button>
                  )}
                </div>
                <div className="px-4 py-3 max-h-[400px] overflow-y-auto">
                  {!showObjPanel ? (
                    <>
                      <p className="text-xs text-slate-700 whitespace-pre-line leading-relaxed">{activeScript.content}</p>
                      {activeScript.hints && (
                        <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                          <strong>Tips:</strong> {activeScript.hints}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-3">
                      {(activeScript.objection_rebuttals || []).map((o, i) => (
                        <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                          <div className="px-3 py-2 bg-red-50 text-xs font-bold text-red-700">"{o.objection}"</div>
                          <div className="px-3 py-2 text-xs text-slate-700 leading-relaxed">{o.rebuttal}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =============== HISTORY MODE =============== */}
      {mode === 'history' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
            <div className="relative flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search calls..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
              {['Date', 'Contact', 'Phone', 'Suburb', 'Roof', 'Outcome', 'Booking', ''].map(h => <th key={h} className="text-left px-4 py-2.5">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {calls.map(c => (
                <tr key={c._id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">{c.call_date}</td>
                  <td className="px-4 py-2 font-medium">{c.contact_name}</td>
                  <td className="px-4 py-2 text-slate-600">{c.phone_number || c.mobile_number}</td>
                  <td className="px-4 py-2 text-slate-500">{c.suburb}</td>
                  <td className="px-4 py-2 text-slate-500">{c.roof_type}</td>
                  <td className="px-4 py-2"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${c.outcome === 'Appointment Booked' ? 'bg-green-100 text-green-700' : c.outcome === 'Not Interested' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>{c.outcome}</span></td>
                  <td className="px-4 py-2 text-slate-500">{c.booking_date || '-'}</td>
                  <td className="px-4 py-2"><div className="flex gap-1"><button onClick={() => editCall(c)} className="p-1 text-slate-400 hover:text-[#D35400]"><Edit2 size={12} /></button><button onClick={() => deleteCall(c._id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={12} /></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {pages > 1 && <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between"><span className="text-xs text-slate-400">Page {page}/{pages}</span><div className="flex gap-1"><button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1 rounded border disabled:opacity-30"><ChevronLeft size={14} /></button><button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1 rounded border disabled:opacity-30"><ChevronRight size={14} /></button></div></div>}
        </div>
      )}

      {/* =============== HINTS MODE =============== */}
      {mode === 'hints' && <TelemarketingHintsView />}

      {/* =============== REPORTS MODE =============== */}
      {mode === 'reports' && <TelemarketingKpiDashboard />}

      {/* =============== SCRIPTS MODE =============== */}
      {mode === 'scripts' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={() => { setScriptForm({ title: '', script_type: 'Cold Call', content: '', hints: '', sort_order: scripts.length + 1, active: true }); setShowScriptForm(true); }} className="flex items-center gap-1.5 px-3 py-2 bg-[#D35400] text-white rounded-lg text-xs font-medium hover:bg-[#A04000]"><Plus size={13} /> Add Script</button>
          </div>
          {scripts.map(s => (
            <div key={s._id} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2"><BookOpen size={15} className="text-[#D35400]" /><span className="text-sm font-bold text-[#0F172A]">{s.title}</span>{s.script_type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{s.script_type}</span>}</div>
                <div className="flex gap-1"><button onClick={() => { setScriptForm({ ...s }); setShowScriptForm(true); }} className="p-1.5 text-slate-400 hover:text-[#D35400]"><Edit2 size={13} /></button><button onClick={async () => { if (window.confirm('Delete?')) { await api.delete(`/telemarketing/scripts/${s._id}`); load(); } }} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button></div>
              </div>
              <p className="text-xs text-slate-600 whitespace-pre-line leading-relaxed">{s.content}</p>
              {s.hints && <div className="mt-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-800"><strong>Tips:</strong> {s.hints}</div>}
              {s.objection_rebuttals?.length > 0 && (
                <div className="mt-3 space-y-2">
                  <h4 className="text-xs font-bold text-slate-500 uppercase">Objection Rebuttals ({s.objection_rebuttals.length})</h4>
                  {s.objection_rebuttals.map((o, i) => (
                    <div key={i} className="text-xs border-l-2 border-red-300 pl-3"><span className="font-bold text-red-600">"{o.objection}"</span><br /><span className="text-slate-600">{o.rebuttal}</span></div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {showScriptForm && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowScriptForm(false)}>
              <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <form onSubmit={saveScript} className="p-5 space-y-3">
                  <h2 className="text-base font-bold">{scriptForm._id ? 'Edit' : 'New'} Script</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <input required value={scriptForm.title || ''} onChange={e => setScriptForm({ ...scriptForm, title: e.target.value })} placeholder="Title *" className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                    <select value={scriptForm.script_type || ''} onChange={e => setScriptForm({ ...scriptForm, script_type: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm"><option value="">Type</option>{['Cold Call', 'Follow-Up', 'Closing', 'Special Offer'].map(t => <option key={t}>{t}</option>)}</select>
                  </div>
                  <textarea value={scriptForm.content || ''} onChange={e => setScriptForm({ ...scriptForm, content: e.target.value })} rows={10} placeholder="Script content..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
                  <textarea value={scriptForm.hints || ''} onChange={e => setScriptForm({ ...scriptForm, hints: e.target.value })} rows={2} placeholder="Tips / Hints..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowScriptForm(false)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm">Cancel</button><button type="submit" className="px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium"><Save size={13} className="inline mr-1" />Save</button></div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
